import React from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Tag, Heart } from "lucide-react";
import { CATEGORY_I18N, getCategoryLabel as getCategoryLabelFromI18n } from "../i18n/categories";
import { localizeLocationValue } from "../utils/locationDisplay";

// Category translations now imported from centralized i18n/categories.js
// Using CATEGORY_I18N for consistent translations across the app

/**
 * Get localized location label.
 * Priority: structured bilingual fields → catalog-wide resolver
 * (utils/locationDisplay) with legacy-alias + AR-mode no-English rules.
 * Accepts ad objects with { city, location, city_ar, city_en, country, ... }.
 */
export function getLocalizedLocationLabel(ad, locale) {
  const isAr = String(locale || "").toLowerCase().startsWith("ar");
  const lang = isAr ? 'ar' : 'en';

  // 1. Prefer explicit localized fields from backend
  const arCity = ad?.location_ar || ad?.city_ar || ad?.locationLabel_ar;
  const enCity = ad?.location_en || ad?.city_en || ad?.locationLabel_en;
  if (isAr && arCity) return arCity;
  if (!isAr && enCity) return enCity;

  const countryCode = ad?.country || ad?.countryCode || ad?.country_code || '';

  // 2. Structured location object → region/city/area parts
  const locObj = ad?.location;
  if (locObj && typeof locObj === 'object') {
    const structured = [locObj.region, locObj.city, locObj.area]
      .filter((p) => typeof p === 'string' && p.trim())
      .join(', ');
    const source = structured || (isAr ? locObj.city_ar : locObj.city_en) || locObj.city_ar || locObj.city_en || '';
    return localizeLocationValue(source, lang, { countryCode });
  }

  // 3. Raw string value through the catalog-wide resolver
  const rawValue = ad?.city || (typeof locObj === 'string' ? locObj : '') || ad?.locationLabel || "";
  const raw = typeof rawValue === 'string' ? rawValue.trim() : "";
  if (!raw) return isAr ? (arCity || '') : (enCity || '');

  return localizeLocationValue(raw, lang, { countryCode });
}

/**
 * Get localized category label
 * Uses centralized CATEGORY_I18N for consistent translations
 */
export function getLocalizedCategoryLabel(ad, locale) {
  const isAr = String(locale || "").toLowerCase().startsWith("ar");
  const lang = isAr ? 'ar' : 'en';

  // Prefer backend localized labels if present
  if (isAr) {
    if (ad?.categoryLabel_ar) return ad.categoryLabel_ar;
    if (ad?.hub_category_main_ar) return ad.hub_category_main_ar;
    if (ad?.category_ar) return ad.category_ar;
  } else {
    if (ad?.categoryLabel_en) return ad.categoryLabel_en;
    if (ad?.hub_category_main_en) return ad.hub_category_main_en;
    if (ad?.category_en) return ad.category_en;
  }
  
  // Use centralized i18n translation
  const categoryKey = ad?.category || ad?.categoryLabel || "";
  return getCategoryLabelFromI18n(categoryKey, lang);
}

/**
 * Chip component with micro-animations
 */
function Chip({ tone = "gray", icon, text }) {
  const toneStyles = {
    gray: {
      border: "rgba(0,0,0,0.08)",
      background: "#f8fafc",
      color: "rgba(0,0,0,0.7)",
      iconColor: "#64748b",
    },
    amber: {
      border: "rgba(245,158,11,0.25)",
      background: "rgba(245,158,11,0.08)",
      color: "#92400e",
      iconColor: "#d97706",
    },
    pink: {
      border: "rgba(244,114,182,0.3)",
      background: "rgba(244,114,182,0.08)",
      color: "#9d174d",
      iconColor: "#db2777",
    },
  }[tone];

  return (
    <div
      data-testid={`chip-${tone}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        borderRadius: 999,
        border: `1px solid ${toneStyles.border}`,
        background: toneStyles.background,
        padding: "8px 12px",
        cursor: "default",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = "scale(0.98)";
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
    >
      <span style={{ color: toneStyles.iconColor, display: "flex", opacity: 0.85 }}>
        {icon}
      </span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 800,
          color: toneStyles.color,
        }}
      >
        {text}
      </span>
    </div>
  );
}

/**
 * Premium Ad Info Card Component
 * Displays location, category, hub badge, and description in a clean, modern card
 */
export default function AdInfoCard({
  locationText,
  categoryText,
  hubType,  // "ladies" | null
  description,
  showBuyerProtectionChip = false, // IMPORTANT: set to false to avoid duplicate
  className = "",
}) {
  const { i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith("ar");

  // Hub display logic
  const hubText = hubType === "ladies" 
    ? (isArabic ? "قسم السيدات" : "Ladies Hub")
    : null;

  return (
    <section
      dir={isArabic ? "rtl" : "ltr"}
      className={className}
      data-testid="ad-info-card"
      style={{
        width: "100%",
        maxWidth: 720,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          padding: "16px",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)";
        }}
      >
        {/* Header - NO Buyer Protection chip (removed duplicate) */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: isArabic ? "row-reverse" : "row",
          }}
        >
          <h2
            data-testid="info-card-title"
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 800,
              color: "rgba(0,0,0,0.88)",
              fontFamily: isArabic ? "'Cairo', sans-serif" : "'Poppins', sans-serif",
            }}
          >
            {isArabic ? "معلومات الإعلان" : "Ad Information"}
          </h2>

          {/* IMPORTANT: Buyer Protection chip removed to avoid duplicate
              Keep ONLY the big Buyer Protection section elsewhere on page */}
          {showBuyerProtectionChip && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11,
                fontWeight: 700,
                padding: "5px 10px",
                borderRadius: 20,
                background: "rgba(46,125,92,0.08)",
                color: "#1F6F3A",
                border: "1px solid rgba(46,125,92,0.15)",
              }}
            >
              <span>{isArabic ? "حماية المشتري" : "Buyer Protection"}</span>
            </div>
          )}
        </div>

        {/* Divider */}
        <div
          style={{
            marginTop: 12,
            height: 1,
            background: "rgba(0,0,0,0.06)",
          }}
        />

        {/* Meta Chips Row */}
        <div
          style={{
            marginTop: 12,
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            justifyContent: "flex-start",
          }}
        >
          {/* Category chip - first for visual hierarchy */}
          {!!categoryText && (
            <Chip 
              tone="amber" 
              icon={<Tag size={15} />} 
              text={categoryText} 
            />
          )}

          {/* Location chip */}
          {!!locationText && (
            <Chip 
              tone="gray" 
              icon={<MapPin size={15} />} 
              text={locationText} 
            />
          )}

          {/* Hub chip (Ladies Hub) */}
          {!!hubText && (
            <Chip 
              tone="pink" 
              icon={<Heart size={15} fill="#db2777" />} 
              text={hubText} 
            />
          )}
        </div>

        {/* Description */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: "rgba(0,0,0,0.8)",
              marginBottom: 8,
              fontFamily: isArabic ? "'Cairo', sans-serif" : "'Poppins', sans-serif",
            }}
          >
            {isArabic ? "الوصف" : "Description"}
          </div>

          <p
            data-testid="ad-description"
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.7,
              color: "rgba(0,0,0,0.65)",
              fontFamily: isArabic ? "'Cairo', sans-serif" : "'Poppins', sans-serif",
              whiteSpace: "pre-wrap",
            }}
          >
            {description || (isArabic ? "لا يوجد وصف بعد." : "No description yet.")}
          </p>
        </div>
      </div>
    </section>
  );
}
